import React, { ReactNode } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';

interface CanProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  role?: string | string[];
  resource?: string;
  action?: string;
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Can component - Conditionally renders children based on permissions
 *
 * @example
 * <Can permission="create_order">
 *   <Button>Create Order</Button>
 * </Can>
 *
 * <Can permissions={["view_orders", "create_order"]} requireAll>
 *   <Button>Advanced Actions</Button>
 * </Can>
 *
 * <Can resource="order" action="create">
 *   <Button>Create Order</Button>
 * </Can>
 *
 * <Can role="administrator">
 *   <AdminPanel />
 * </Can>
 */
const Can: React.FC<CanProps> = ({
  permission,
  permissions,
  requireAll = false,
  role,
  resource,
  action,
  fallback = null,
  children,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, canAccess } =
    usePermissions();

  // Only Super Admin bypasses permission checks
  // Administrators must have explicit permissions
  if (hasRole('superadministrator')) {
    return <>{children}</>;
  }

  let hasAccess = false;

  // Check by role
  if (role) {
    hasAccess = hasRole(role);
  }
  // Check by single permission
  else if (permission) {
    hasAccess = hasPermission(permission);
  }
  // Check by multiple permissions
  else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  }
  // Check by resource and action
  else if (resource && action) {
    hasAccess = canAccess(resource, action);
  }
  // If no condition provided, deny access
  else {
    hasAccess = false;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

export default Can;
